<template>
  <div class="text-center flex flex-col items-center">
    <NuxtLink to="/" class="flex w-full justify-center text-center">
    <Logo class="w-1/2 mb-12"/>
    </NuxtLink>
    <h1 class="text-2xl text-white font-semibold mb-6">
      Welcome to<br/> The Kudo Sales DEMO Site
    </h1>

    <!-- Redan inloggad -->
    <div
      v-if="auth.isAuthenticated"
      class="bg-green-50 text-green-700 p-3 text-sm mb-6 flex flex-col gap-2 items-center"
    >
      <span>Logged in as {{ auth.user?.email }}.</span>
    </div>

    <!-- Felmeddelande -->
    <div v-motion-pop
      v-if="error"
      class="w-5/12 bg-red-50/50 text-red-700 p-3 text-sm mb-4"
    >
      {{ error }}
    </div>

    <!-- Formulär (visas bara om ej inloggad) -->
    <form
      v-if="!auth.isAuthenticated"
      class="space-y-4 mb-2 flex flex-col items-center"
      @submit.prevent="onEmailLogin"
    >
      <UInput
        v-model="email"
        type="email"
        placeholder="E-mail"
        autocomplete="email"
        required
      />
      <UInput
        v-model="password"
        type="password"
        placeholder="Password"
        autocomplete="current-password"
        required
      />
      <div class="flex gap-2">
        <UButton class="w-full text-center cursor-pointer"
          icon="i-lucide-log-in"
          type="submit"
          :loading="loadingEmail"
          :disabled="loadingGoogle || loadingEmail"
        >
          Log In with email
        </UButton>
      </div>
      <div class="w-full text-center">
        <button type="link" class="text-xs text-white text-center hover:underline cursor-pointer" @click="onForgotPassword">
          Forgot password?
        </button>
      </div>
    </form>

    <div v-if="!auth.isAuthenticated" class="mb-4 text-center text-xs  uppercase tracking-wide text-white">
      OR
    </div>

    <div v-if="!auth.isAuthenticated" class="flex flex-col gap-3 items-center">
      <UButton
        color="primary"
        :loading="loadingGoogle"
        :disabled="loadingEmail"
        class="cursor-pointer"
        icon="i-lucide-log-in"
        @click="onGoogle"
      >
        Log-in with Google
      </UButton>
      <p class="text-xs text-white">By logging in you approve our cookie policy.<br/><br/>
      Contact <a href="mailto:teodor@hjartat.net">teodor@hjartat.net</a> for support</p>
    </div>

    <UModal v-model:open="cartChoiceModalOpen" :dismissible="false" :close="false">
      <template #content>
        <div class="p-6 text-left space-y-3">
          <h3 class="text-lg font-semibold">Choose cart to keep</h3>
          <p class="text-sm text-gray-700">
            We found items in your current cart and in your saved account cart.
          </p>
          <p class="text-sm text-gray-700">
            Do you want to keep the cart from before login, or replace it with your saved cart?
          </p>
          <div class="flex flex-col sm:flex-row gap-2 pt-2">
            <UButton
              color="primary"
              icon="i-lucide-shopping-cart"
              :loading="resolvingCartChoice"
              @click="keepGuestCart"
            >
              Keep pre-login cart
            </UButton>
            <UButton
              color="neutral"
              variant="soft"
              icon="i-lucide-refresh-cw"
              :loading="resolvingCartChoice"
              @click="useSavedCart"
            >
              Use saved account cart
            </UButton>
          </div>
        </div>
      </template>
    </UModal>
  </div>
</template>

<script setup lang="ts">
import { ref, computed, watch } from 'vue'
import { useAuthStore } from '@/store/auth'
import { useCartStore } from '@/store/cart'
import { usePlausible } from '@/composables/usePlausible'

interface PersistCartShape {
  v: number
  saved: (string | number)[]
  items: Record<string, { qty: number }>
}

const props = withDefaults(defineProps<{
  redirect?: string
  autoRedirect?: boolean
}>(), {
  redirect: '/',
  autoRedirect: true,
  showTitle: true,
  title: 'Log in'
})

const emit = defineEmits<{
  (e: 'logged-in'): void
}>()

const auth = useAuthStore()
const cart = useCartStore()
const route = useRoute()
const router = useRouter()
const { trackEvent } = usePlausible()

const effectiveRedirect = computed(() =>
  String(route.query.redirect || props.redirect || '/products')
)

const email = ref('')
const password = ref('')
const error = ref<string | null>(null)

const loadingGoogle = ref(false)
const loadingEmail = ref(false)
const loadingForgot = ref(false)

const cartChoiceModalOpen = ref(false)
const resolvingCartChoice = ref(false)
const pendingRemoteCart = ref<PersistCartShape | null>(null)
const continueAfterCartSelection = ref(false)

const { $loginWithGoogle, $loginWithEmail, $logoutFirebase, $sendPasswordResetEmail } = useNuxtApp()

function emitLoggedIn() {
  emit('logged-in')
}

watch(
  () => auth.isAuthenticated,
  (isAuth) => {
    if (!isAuth || continueAfterCartSelection.value) return
    if (props.autoRedirect) {
      emitLoggedIn()
      router.push(effectiveRedirect.value)
    }
  },
  { immediate: true }
)

function getItemCount(data: PersistCartShape | null): number {
  if (!data) return 0
  return Object.values(data.items || {}).reduce((sum, item) => sum + Math.max(0, item.qty || 0), 0)
}

function getCurrentCartSnapshot(): PersistCartShape {
  return cart.toPersistShape()
}

async function handleCartOnLogin(preLoginCart: PersistCartShape) {
  const userKey = auth.user?.uid || auth.user?.email
  if (!userKey) return

  const remoteCart = await cart.fetchFirestoreCart(userKey)
  const localItems = getItemCount(preLoginCart)
  const remoteItems = getItemCount(remoteCart as PersistCartShape | null)

  if (remoteCart && localItems > 0 && remoteItems > 0) {
    pendingRemoteCart.value = remoteCart as PersistCartShape
    cartChoiceModalOpen.value = true
    continueAfterCartSelection.value = true
    return
  }

  if (remoteCart && remoteItems > 0) {
    cart.applyPersistShape(remoteCart)
  } else {
    cart.applyPersistShape(preLoginCart)
    await cart.persistToFirestore(userKey)
  }

  finalizeLoginFlow()
}

function finalizeLoginFlow() {
  continueAfterCartSelection.value = false
  if (props.autoRedirect) {
    emitLoggedIn()
    router.push(effectiveRedirect.value)
  } else {
    emitLoggedIn()
  }
}

async function keepGuestCart() {
  const userKey = auth.user?.uid || auth.user?.email
  if (!userKey) return
  resolvingCartChoice.value = true
  try {
    await cart.persistToFirestore(userKey)
    cartChoiceModalOpen.value = false
    pendingRemoteCart.value = null
    finalizeLoginFlow()
  } finally {
    resolvingCartChoice.value = false
  }
}

function useSavedCart() {
  if (!pendingRemoteCart.value) return
  cart.applyPersistShape(pendingRemoteCart.value)
  cartChoiceModalOpen.value = false
  pendingRemoteCart.value = null
  finalizeLoginFlow()
}

async function onGoogle() {
  error.value = null
  loadingGoogle.value = true
  const preLoginCart = getCurrentCartSnapshot()
  try {
    await $loginWithGoogle()
    await handleCartOnLogin(preLoginCart)
    trackEvent('Login', { method: 'google', email: auth.user?.email || '' })
  } catch (e: any) {
    error.value = readable(e)
    continueAfterCartSelection.value = false
  } finally {
    loadingGoogle.value = false
  }
}

async function onEmailLogin() {
  error.value = null
  loadingEmail.value = true
  const preLoginCart = getCurrentCartSnapshot()
  try {
    await $loginWithEmail(email.value, password.value)
    await handleCartOnLogin(preLoginCart)
    trackEvent('Login', { method: 'email', email: email.value })
  } catch (e: any) {
    error.value = readable(e)
    continueAfterCartSelection.value = false
  } finally {
    loadingEmail.value = false
  }
}

async function onForgotPassword() {
  error.value = null
  loadingForgot.value = true
  try {
    if (!email.value) {
      error.value = 'Enter your e-mail above and click again.'
      return
    }
    await $sendPasswordResetEmail(email.value)
    error.value = 'Password reset email sent! Check your inbox.'
  } catch (e: any) {
    error.value = readable(e)
  } finally {
    loadingForgot.value = false
  }
}

async function logout() {
  await $logoutFirebase()
  error.value = null
}

function readable(e: any) {
  const code = e?.code || ''
  switch (code) {
    case 'auth/invalid-email': return 'Invalid e-mail'
    case 'auth/wrong-password': return 'Bad password'
    case 'auth/user-not-found': return 'User not found'
    case 'auth/email-already-in-use': return 'E-mail already in use'
    case 'auth/weak-password': return 'Too weak password'
    case 'auth/popup-closed-by-user': return 'Cancelled log-in'
    case 'auth/missing-email': return 'Enter your e-mail above and click again.'
  }
  return e?.message || 'En error occurred'
}
</script>
